import type { GameState, Telegraph } from '@gra/engine';
import {
  CircleGeometry,
  DoubleSide,
  Fog,
  Group,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  RingGeometry,
  type BufferGeometry,
  type Scene,
} from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { Environment } from './environment';
import { WORLD_SCALE as S } from './visuals';

const DANGER = '#ff3b2f';
/** Wysokość zapowiedzi nad ziemią (nad kałużami, pod postaciami). */
const Y = 0.05;

interface TelegraphView {
  root: Group;
  fill: Mesh;
  rim: Mesh;
  rimMat: MeshBasicMaterial;
  fillMat: MeshBasicMaterial;
}

/**
 * Klimat walki z bossem: zapowiedzi ciosów na ziemi (krąg, stożek, pas szarży, salwa), przygaszone słońce,
 * gdy boss żyje, i mgła w czasie wydarzenia „Mgła nadciąga”. Tylko czyta stan gry.
 */
export class BossView {
  private readonly views = new Map<number, TelegraphView>();
  private readonly baseSun: number;
  private readonly baseFog: { near: number; far: number } | null;
  private dim = 1;
  private fogK = 0;

  constructor(
    private readonly scene: Scene,
    private readonly state: GameState,
    private readonly env: Environment,
  ) {
    this.baseSun = env.sun.intensity;
    const fog = scene.fog;
    this.baseFog = fog instanceof Fog ? { near: fog.near, far: fog.far } : null;
  }

  update(dt: number): void {
    const s = this.state;
    const alive = new Set<number>();
    for (const t of s.telegraphs) {
      alive.add(t.id);
      let view = this.views.get(t.id);
      if (!view) {
        view = this.create(t);
        this.views.set(t.id, view);
      }
      this.sync(t, view);
    }
    for (const [id, view] of this.views) {
      if (alive.has(id)) continue;
      this.disposeView(view);
      this.views.delete(id);
    }

    // Boss na polu: słońce przygasa, robi się ciężko i ponuro.
    const dimTarget = s.bossIds.length > 0 ? 0.62 : 1;
    this.dim += (dimTarget - this.dim) * Math.min(1, dt * 1.5);
    this.env.sun.intensity = this.baseSun * this.dim;

    // Mgła: horyzont przysuwa się do bohatera, potem wolno wraca.
    const fogTarget = s.runEvent?.kind === 'fog' ? 1 : 0;
    this.fogK += (fogTarget - this.fogK) * Math.min(1, dt * 0.8);
    const fog = this.scene.fog;
    if (this.baseFog && fog instanceof Fog) {
      fog.near = this.baseFog.near * (1 - this.fogK * 0.85);
      fog.far = this.baseFog.far * (1 - this.fogK * 0.62);
    }
  }

  private create(t: Telegraph): TelegraphView {
    const root = new Group();
    const rimMat = new MeshBasicMaterial({ color: DANGER, transparent: true, opacity: 0.85, depthWrite: false, side: DoubleSide, toneMapped: false });
    const fillMat = new MeshBasicMaterial({ color: DANGER, transparent: true, opacity: 0.32, depthWrite: false, side: DoubleSide, toneMapped: false });
    let rimGeo: BufferGeometry;
    let fillGeo: BufferGeometry;
    if (t.shape === 'circle') {
      const r = t.radius * S;
      rimGeo = new RingGeometry(r * 0.94, r, 48).rotateX(Math.PI / 2);
      fillGeo = new CircleGeometry(r, 48).rotateX(Math.PI / 2);
    } else if (t.shape === 'cone') {
      const r = t.radius * S;
      // Kształt w płaszczyźnie XY obrócony tak, że kąt z silnika (x, y) trafia w (x, z) świata.
      rimGeo = new RingGeometry(r * 0.95, r, 40, 1, t.angle - t.arc / 2, t.arc).rotateX(Math.PI / 2);
      fillGeo = new CircleGeometry(r, 40, t.angle - t.arc / 2, t.arc).rotateX(Math.PI / 2);
    } else if (t.shape === 'line') {
      const len = t.length * S;
      const w = t.width * S;
      rimGeo = outline(len, w).rotateX(Math.PI / 2);
      fillGeo = new PlaneGeometry(1, w).translate(0.5, 0, 0).rotateX(Math.PI / 2);
      root.rotation.y = -t.angle;
    } else {
      // Salwa: pierścień zaciskający się wokół bossa.
      const r = 2.2;
      rimGeo = new RingGeometry(r * 0.9, r, 48).rotateX(Math.PI / 2);
      fillGeo = new RingGeometry(r * 0.2, r * 0.3, 48).rotateX(Math.PI / 2);
      rimMat.color.set('#ffb45a');
      fillMat.color.set('#ffb45a');
    }
    const rim = new Mesh(rimGeo, rimMat);
    const fill = new Mesh(fillGeo, fillMat);
    rim.renderOrder = fill.renderOrder = 3;
    root.add(fill, rim);
    root.position.set(t.x * S, Y, t.y * S);
    this.scene.add(root);
    return { root, fill, rim, rimMat, fillMat };
  }

  private sync(t: Telegraph, v: TelegraphView): void {
    const k = Math.min(1, 1 - t.left / t.duration);
    // Pulsowanie przyspiesza pod koniec — cios zaraz spadnie.
    const pulse = 0.75 + 0.25 * Math.sin((1 - t.left) * (8 + k * 18));
    v.rimMat.opacity = 0.55 + 0.4 * pulse;
    if (t.shape === 'circle' || t.shape === 'cone') {
      v.fill.scale.set(Math.max(0.02, k), 1, Math.max(0.02, k));
      v.fillMat.opacity = 0.18 + 0.3 * k;
    } else if (t.shape === 'line') {
      v.fill.scale.set(Math.max(0.02, k * t.length * S), 1, 1);
      v.fillMat.opacity = 0.2 + 0.3 * k;
    } else {
      const owner = this.state.enemies.find((e) => e.id === t.ownerId);
      if (owner) v.root.position.set(owner.x * S, Y, owner.y * S);
      const shrink = 1 - k * 0.65;
      v.rim.scale.set(shrink, 1, shrink);
      v.fillMat.opacity = 0.25 + 0.5 * k;
    }
  }

  private disposeView(v: TelegraphView): void {
    v.root.removeFromParent();
    v.rim.geometry.dispose();
    v.fill.geometry.dispose();
    v.rimMat.dispose();
    v.fillMat.dispose();
  }

  dispose(): void {
    for (const v of this.views.values()) this.disposeView(v);
    this.views.clear();
  }
}

/** Obrys prostokąta (pas szarży) jako cienka ramka zaczynająca się w (0, 0). */
function outline(len: number, w: number): BufferGeometry {
  const t = 0.08;
  const parts = [
    new PlaneGeometry(len, t).translate(len / 2, w / 2 - t / 2, 0),
    new PlaneGeometry(len, t).translate(len / 2, -w / 2 + t / 2, 0),
    new PlaneGeometry(t, w).translate(len - t / 2, 0, 0),
  ];
  const merged = mergeGeometries(parts)!;
  for (const g of parts) g.dispose();
  return merged;
}
